import { Injectable, TemplateRef, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';

export const APP_NAME = 'Propraize';

/**
 * Tracks the current page's heading/description for the app shell's calcite-panel.
 *
 * Defaults come from the active route's `title`/`data.summary`, resolved on every
 * navigation by `RouteInfoTitleStrategy` (Angular's `TitleStrategy` extension point —
 * see `routing/route-info-title-strategy.ts`), which walks the route tree so a page
 * inherits `data.summary` from the nearest ancestor that has one and concatenates
 * `title` up the tree for pages that don't set their own dynamically. Routes with a
 * dynamic title (e.g. a loaded record's name) opt out via `DYNAMIC_TITLE` (see that
 * file) so the strategy skips them, and the routed component calls `heading.set(...)`
 * itself.
 *
 * `headerActions` is a bridge for the panel's `header-actions-end` slot: the panel lives
 * in `AppShellComponent`, an ancestor that never re-renders per route, so a routed page's
 * own template content is nested one level too deep (inside its own host element, a
 * sibling of `<router-outlet>`) to land as a *direct* child of the panel — which is what
 * native slot distribution requires. A page that needs header actions declares them in an
 * `<ng-template #headerActions>`, registers the `TemplateRef` here, and `AppShellComponent`
 * stamps it via `NgTemplateOutlet` directly inside the panel, restoring that direct-child
 * relationship. Pages must clear this (`.set(null)`) on destroy so a stale template isn't
 * left registered after navigating away.
 */
@Injectable({
  providedIn: 'root',
})
export class PageInfoService {
  private readonly titleService = inject(Title);

  readonly heading = signal<string>(APP_NAME);
  readonly description = signal<string>('');
  readonly headerActions = signal<TemplateRef<unknown> | null>(null);

  constructor() {
    effect(() => {
      const heading = this.heading();
      // Avoid "Propraize - Propraize" when heading is empty or already the app name
      // (the initial signal value, or RouteInfoTitleStrategy's no-title-found fallback).
      this.titleService.setTitle(heading && heading !== APP_NAME ? `${heading} - ${APP_NAME}` : APP_NAME);
    });
  }
}
